/**
 * Simple obfuscation utility for request payloads
 * Uses base64 encoding + XOR cipher to avoid control character issues
 */

// Secret key - in production, this should be different from backend
// Note: Frontend code is always visible, so this is obfuscation, not true security
const OBFUSCATION_KEY = process.env.REACT_APP_OBFUSCATION_KEY || 'x-dm-automation-secret-key-2024';

/**
 * XOR cipher on a string - works with base64 strings to avoid control characters
 */
function xorCipherString(str, key) {
  let result = '';
  for (let i = 0; i < str.length; i++) {
    const charCode = str.charCodeAt(i);
    const keyChar = key.charCodeAt(i % key.length);
    // XOR and ensure result is in printable ASCII range (32-126) or base64 range
    const xorResult = charCode ^ keyChar;
    // Keep it in safe range for base64 characters
    result += String.fromCharCode(xorResult & 0xFF);
  }
  return result;
}

/**
 * Obfuscate data before sending to server
 */
export function obfuscate(data) {
  try {
    // Step 1: Convert to JSON string
    const jsonString = JSON.stringify(data);
    
    // Step 2: Base64 encode the JSON (this gives us safe characters)
    // Use unescape/encodeURIComponent for proper UTF-8 to base64 conversion
    const base64Json = btoa(unescape(encodeURIComponent(jsonString)));
    
    // Step 3: Apply XOR cipher on the base64 string
    // Since base64 only uses A-Z, a-z, 0-9, +, /, =, XOR will still produce valid characters
    const obfuscated = xorCipherString(base64Json, OBFUSCATION_KEY);
    
    // Step 4: Base64 encode again to make it safe for JSON transport
    return btoa(obfuscated);
  } catch (error) {
    console.error('Error obfuscating data:', error);
    throw error;
  }
}

/**
 * Deobfuscate data received from server (if needed)
 */
export function deobfuscate(obfuscatedData) {
  try {
    // Step 1: Base64 decode the outer layer
    const encrypted = atob(obfuscatedData);
    
    // Step 2: Apply XOR cipher (XOR is symmetric) to get base64 JSON
    const base64Json = xorCipherString(encrypted, OBFUSCATION_KEY);
    
    // Step 3: Base64 decode to get original JSON string
    const jsonString = decodeURIComponent(escape(atob(base64Json)));
    
    // Step 4: Parse JSON
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Error deobfuscating data:', error);
    throw error;
  }
}

